import { useMemo } from 'react';
import { GetDocumentResponse } from 'redux/services/documentRepository/types';
import { hasDecryptionError, hasAntiVirusError, getErrorMessage, ValidationStatuses } from 'utils/statusHelpers';

interface ValidationErrorDetail {
    message?: string;
    code?: string;
}

interface UsePayrollErrorsProps {
    payrollError: any;
    uploadPayrollFileError: any;
    payrollDocumentDetailError: any;
    presignPayrollError: any;
    uploadPayrollFileServiceError: any;
    payrollDocumentDetail?: GetDocumentResponse;
    canReloadRequestDetails: boolean;
    // APEX-1151: Validation props
    validationResults?: unknown;
    isValidationEnabled?: boolean;
}

interface UsePayrollErrorsReturn {
    apiErrors: string[];
    fileProcessingErrors: string[];
    hasApiErrors: boolean;
    hasFileProcessingErrors: boolean;
    hasVirusError: boolean;
    hasDecryptError: boolean;
}

export const usePayrollErrors = ({
    payrollError,
    uploadPayrollFileError,
    payrollDocumentDetailError,
    presignPayrollError,
    uploadPayrollFileServiceError,
    payrollDocumentDetail,
    canReloadRequestDetails,
    validationResults,
    isValidationEnabled,
}: UsePayrollErrorsProps): UsePayrollErrorsReturn => {
    // Check if any API error exists
    const hasApiErrors = !!(
        payrollError ||
        uploadPayrollFileError ||
        payrollDocumentDetailError ||
        presignPayrollError ||
        uploadPayrollFileServiceError
    );

    // Aggregate API errors with fallback for errors without arrays
    const apiErrors = useMemo(() => {
        if (!hasApiErrors) {
            return [];
        }

        // First, try to extract errors from error.data.errors arrays
        const structuredErrors = [
            ...(payrollError?.data?.errors ?? []),
            ...(uploadPayrollFileError?.data?.errors ?? []),
            ...(payrollDocumentDetailError?.data?.errors ?? []),
            ...(presignPayrollError?.data?.errors ?? []),
            ...(uploadPayrollFileServiceError?.data?.errors ?? []),
        ].map((err: any) => err.message);

        // If we have structured errors, return them
        if (structuredErrors.length > 0) {
            return structuredErrors;
        }

        // Otherwise, create fallback messages for errors without arrays
        // (e.g., network failures, 5xx responses)
        const fallbackErrors: string[] = [];

        if (payrollError) {
            fallbackErrors.push(
                payrollError?.data?.message ||
                payrollError?.message ||
                'Failed to load payroll data'
            );
        }
        if (uploadPayrollFileError) {
            fallbackErrors.push(
                uploadPayrollFileError?.data?.message ||
                uploadPayrollFileError?.message ||
                'Failed to upload payroll file'
            );
        }
        if (payrollDocumentDetailError) {
            fallbackErrors.push(
                payrollDocumentDetailError?.data?.message ||
                payrollDocumentDetailError?.message ||
                'Failed to load document details'
            );
        }
        if (presignPayrollError) {
            fallbackErrors.push(
                presignPayrollError?.data?.message ||
                presignPayrollError?.message ||
                'Failed to generate upload URL'
            );
        }
        if (uploadPayrollFileServiceError) {
            fallbackErrors.push(
                uploadPayrollFileServiceError?.data?.message ||
                uploadPayrollFileServiceError?.message ||
                'Failed to upload file to service'
            );
        }

        return fallbackErrors;
    }, [
        hasApiErrors,
        payrollError,
        uploadPayrollFileError,
        payrollDocumentDetailError,
        presignPayrollError,
        uploadPayrollFileServiceError,
    ]);

    // Check file processing status
    const antiVirusStatus = payrollDocumentDetail?.data?.antiVirusStatus;
    const decryptionStatus = payrollDocumentDetail?.data?.decryptionStatus;
    const decryptionError = payrollDocumentDetail?.data?.decryptionError;

    const hasVirusError = hasAntiVirusError(antiVirusStatus);
    const hasDecryptError = hasDecryptionError(decryptionStatus);

    // Extract file processing errors
    const fileProcessingErrors = useMemo(() => {
        if (!canReloadRequestDetails) {
            return [];
        }

        const errors: string[] = [];

        if (hasVirusError) {
            errors.push(getErrorMessage('antiVirus', antiVirusStatus));
        }

        if (hasDecryptError) {
            // Handle both legacy string format and new structured object format
            let errorMessage: string;

            if (typeof decryptionError === 'string') {
                // Legacy format: decryptionError is a plain string
                errorMessage = decryptionError;
            } else {
                // New format: decryptionError is a structured object with userMessage, message, etc.
                errorMessage =
                    decryptionError?.userMessage ||
                    decryptionError?.message ||
                    payrollDocumentDetail?.data?.decryptionUserMessage ||
                    payrollDocumentDetail?.data?.decryptionErrorMessage ||
                    getErrorMessage('decryption', decryptionStatus);
            }

            errors.push(errorMessage);
        }

        // APEX-1151: Extract validation errors when validation fails
        if (isValidationEnabled && validationResults && typeof validationResults === 'object') {
            const validationObj = validationResults as Record<string, unknown>;
            if (validationObj.validationStatus === ValidationStatuses.FAILED) {
                const errorDetails = (validationObj.validationResults as Record<string, unknown> | undefined)?.errorDetails || [];

                // Extract error messages from errorDetails array
                const validationErrors = (errorDetails as ValidationErrorDetail[])
                    .map((detail: ValidationErrorDetail) => detail.message || detail.code)
                    .filter((msg): msg is string => !!msg);

                // Add validation errors to the list
                if (validationErrors.length > 0) {
                    errors.push(...validationErrors);
                } else {
                    // Fallback if no error details provided
                    errors.push('File validation failed. Please check the file format and content.');
                }
            }
        }

        return errors;
    }, [
        canReloadRequestDetails,
        hasVirusError,
        hasDecryptError,
        antiVirusStatus,
        decryptionStatus,
        decryptionError,
        payrollDocumentDetail,
        validationResults,
        isValidationEnabled,
    ]);

    return {
        apiErrors,
        fileProcessingErrors,
        hasApiErrors: apiErrors.length > 0,
        hasFileProcessingErrors: fileProcessingErrors.length > 0,
        hasVirusError,
        hasDecryptError,
    };
};
